// Downloads page — manage offline map regions
// Shows download status, progress, storage usage, and delete controls

import { useState, useEffect, useRef } from 'react';
import { Download, Trash2, HardDrive, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import type { OfflineMap } from '@/lib/world-model';
import {
  saveOfflineMap, getAllOfflineMaps, getOfflineMap, deleteOfflineMap
} from '@/lib/local-db';
import {
  downloadTilesToOPFS, deleteTileFile, hasTileFile, getTileFileSize,
  formatBytes, MAINE_TILE_KEY
} from '@/lib/opfs-tiles';

// Available regions — the only ones we ship for MVP
const AVAILABLE_REGIONS: OfflineMap[] = [
  {
    id: 'maine-v1',
    regionId: 'maine',
    name: 'Southern Maine',
    sizeBytes: 85 * 1024 * 1024, // ~85 MB estimate
    opfsKey: MAINE_TILE_KEY,
    status: 'available',
  },
];

// Where to pull the tiles from — swap to your own R2 bucket in production
// Using Protomaps public planet extract with a Maine-region bounding box
// In production, compile a Maine extract: protomaps extract maine.pmtiles --bbox=...
const TILE_URLS: Record<string, string> = {
  'maine-v1': 'https://build.protomaps.com/20250109.pmtiles',
};

export function DownloadsPage() {
  const [maps, setMaps] = useState<OfflineMap[]>([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const abortRefs = useRef<Record<string, AbortController>>({});

  const refresh = async () => {
    // Merge DB state with available list
    const saved = await getAllOfflineMaps();
    const savedMap: Record<string, OfflineMap> = {};
    saved.forEach(m => { savedMap[m.id] = m; });

    const merged = await Promise.all(
      AVAILABLE_REGIONS.map(async region => {
        const inDb = savedMap[region.id];
        const onDisk = await hasTileFile(region.opfsKey);
        if (inDb && onDisk) {
          const size = await getTileFileSize(region.opfsKey);
          return { ...inDb, status: 'downloaded' as const, sizeBytes: size };
        }
        return { ...region, status: 'available' as const };
      })
    );

    setMaps(merged);

    // Storage estimate
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const est = await navigator.storage.estimate();
      setStorageUsed(est.usage ?? 0);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleDownload = async (region: OfflineMap) => {
    const url = TILE_URLS[region.id];
    if (!url) return;

    const aborter = new AbortController();
    abortRefs.current[region.id] = aborter;

    const updating = { ...region, status: 'downloading' as const, progress: 0 };
    await saveOfflineMap(updating);
    setMaps(prev => prev.map(m => m.id === region.id ? updating : m));

    try {
      await downloadTilesToOPFS(
        url,
        region.opfsKey,
        async (pct, downloaded, total) => {
          const partial: OfflineMap = {
            ...region,
            status: 'downloading',
            progress: pct,
            sizeBytes: total,
          };
          await saveOfflineMap(partial);
          setMaps(prev => prev.map(m => m.id === region.id ? partial : m));
        },
        aborter.signal,
      );

      const finalSize = await getTileFileSize(region.opfsKey);
      const done: OfflineMap = {
        ...region,
        status: 'downloaded',
        progress: 100,
        sizeBytes: finalSize,
        downloadedAt: new Date().toISOString(),
      };
      await saveOfflineMap(done);
      setMaps(prev => prev.map(m => m.id === region.id ? done : m));
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      const failed: OfflineMap = { ...region, status: 'error' };
      await saveOfflineMap(failed);
      setMaps(prev => prev.map(m => m.id === region.id ? failed : m));
    } finally {
      delete abortRefs.current[region.id];
    }

    refresh();
  };

  const handleDelete = async (region: OfflineMap) => {
    if (!confirm(`Delete "${region.name}" offline map? You'll need to redownload it.`)) return;
    await deleteTileFile(region.opfsKey);
    await deleteOfflineMap(region.id);
    refresh();
  };

  const handleCancel = (regionId: string) => {
    abortRefs.current[regionId]?.abort();
    delete abortRefs.current[regionId];
  };

  return (
    <div className="flex flex-col h-full bg-[#000] text-white pb-safe">
      {/* Header */}
      <div className="px-5 pt-14 pb-4 border-b border-[#2c2c2e]">
        <h1 className="text-2xl font-bold">Offline Maps</h1>
        <p className="text-[#8e8e93] text-sm mt-1">
          Download maps to fish without cell service
        </p>
      </div>

      {/* Storage indicator */}
      <div className="flex items-center gap-2 px-5 py-3 bg-[#1c1c1e] border-b border-[#2c2c2e]">
        <HardDrive className="w-4 h-4 text-[#8e8e93]" />
        <span className="text-[#8e8e93] text-sm">
          Storage used: {formatBytes(storageUsed)}
        </span>
      </div>

      {/* Region list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {maps.map(region => (
          <RegionCard
            key={region.id}
            region={region}
            onDownload={() => handleDownload(region)}
            onDelete={() => handleDelete(region)}
            onCancel={() => handleCancel(region.id)}
          />
        ))}
      </div>
    </div>
  );
}

function RegionCard({
  region,
  onDownload,
  onDelete,
  onCancel,
}: {
  region: OfflineMap;
  onDownload: () => void;
  onDelete: () => void;
  onCancel: () => void;
}) {
  const status = region.status;

  return (
    <div className="bg-[#1c1c1e] rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-white font-semibold">{region.name}</h3>
          <p className="text-[#8e8e93] text-sm mt-0.5">
            {status === 'downloaded'
              ? `Downloaded · ${formatBytes(region.sizeBytes)}`
              : `~${formatBytes(region.sizeBytes)}`}
          </p>
          {region.downloadedAt && (
            <p className="text-[#8e8e93] text-xs mt-0.5">
              Updated {new Date(region.downloadedAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <StatusIcon status={status} />
      </div>

      {/* Progress bar */}
      {status === 'downloading' && (
        <div className="space-y-1">
          <div className="h-2 bg-[#2c2c2e] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#1a4a2e] rounded-full transition-all duration-300"
              style={{ width: `${region.progress ?? 0}%` }}
            />
          </div>
          <p className="text-[#8e8e93] text-xs">{region.progress ?? 0}% downloaded</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {status === 'available' || status === 'error' ? (
          <button
            onClick={onDownload}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1a4a2e] text-white text-sm font-semibold"
          >
            <Download className="w-4 h-4" />
            {status === 'error' ? 'Retry Download' : 'Download'}
          </button>
        ) : status === 'downloading' ? (
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl bg-[#d45a1a] text-white text-sm font-semibold"
          >
            Cancel
          </button>
        ) : (
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#2c2c2e] text-[#d45a1a] text-sm font-semibold"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: OfflineMap['status'] }) {
  if (status === 'downloaded') return <CheckCircle className="w-6 h-6 text-[#1a4a2e]" />;
  if (status === 'downloading') return <Loader className="w-6 h-6 text-[#007AFF] animate-spin" />;
  if (status === 'error') return <AlertCircle className="w-6 h-6 text-[#d45a1a]" />;
  return <Download className="w-6 h-6 text-[#8e8e93]" />;
}
